import type { MemePosition } from "@/api/types";

interface MemePositionCardProps {
  position: MemePosition;
}

function formatPrice(value: number): string {
  if (value === 0) return "0";
  if (value >= 1) return value.toFixed(4);
  if (value >= 0.0001) return value.toPrecision(3);
  return value.toExponential(2);
}

function shortAddress(address: string): string {
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

function formatDuration(openedAt: number, closedAt: number | null): string {
  const end = closedAt ?? Date.now();
  const minutes = Math.max(0, Math.floor((end - openedAt) / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

const DISCOVERY_LABEL: Record<MemePosition["discoverySource"], string> = {
  geckoterminal_new_pools: "New pool",
  geckoterminal_trending: "Trending",
};

const STATUS_LABEL: Record<MemePosition["status"], string> = {
  open: "Open",
  closed: "Closed",
  rug: "Rug / written off",
};

export default function MemePositionCard({ position }: MemePositionCardProps) {
  const isOpen = position.status === "open";
  const pnl = position.realizedPnlUsd;
  const changeFromEntryPercent =
    position.entryPriceUsd > 0 ? ((position.highestPriceUsd - position.entryPriceUsd) / position.entryPriceUsd) * 100 : 0;

  return (
    <div className="rounded-xl border border-line bg-panel p-4 shadow-panel">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-semibold text-ink">{position.symbol}</span>
            <span className="rounded border border-line px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-ink-faint">
              BSC
            </span>
            <span className="rounded border border-line-soft px-1.5 py-0.5 font-mono text-[10px] text-ink-faint">
              {DISCOVERY_LABEL[position.discoverySource]}
            </span>
          </div>
          <div className="mt-1 font-mono text-[11px] text-ink-faint">
            {shortAddress(position.tokenAddress)} · {formatDuration(position.openedAt, position.closedAt)}
            {position.status === "rug" && <span className="ml-1.5 text-sell">· {STATUS_LABEL.rug}</span>}
          </div>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide ${position.dryRun ? "bg-paper/10 text-paper" : "bg-live/10 text-live"}`}
        >
          {position.dryRun ? "Paper" : "Live"}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-[11px]">
        <div className="min-w-0">
          <div className="text-ink-faint">Entry (USD)</div>
          <div className="tabular break-all text-ink">${formatPrice(position.entryPriceUsd)}</div>
        </div>
        <div className="min-w-0">
          <div className="text-ink-faint">Highest</div>
          <div className="tabular break-all text-gold">
            ${formatPrice(position.highestPriceUsd)}
            {isOpen && (
              <span className={changeFromEntryPercent >= 0 ? "text-profit" : "text-sell"}>
                {" "}
                ({changeFromEntryPercent >= 0 ? "+" : ""}
                {changeFromEntryPercent.toFixed(0)}%)
              </span>
            )}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-ink-faint">Cost Basis</div>
          <div className="tabular break-all text-ink">${position.costBasisUsd.toFixed(2)}</div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5 font-mono text-[10px] text-ink-dim">
        <span className={`rounded border px-1.5 py-0.5 ${position.tp1Taken ? "border-profit/50 text-profit" : "border-line-soft text-ink-faint"}`}>
          TP1 {position.tp1Taken ? "taken" : "pending"}
        </span>
        <span className="rounded border border-line-soft px-1.5 py-0.5 text-ink-faint">
          {(position.remainingFraction * 100).toFixed(0)}% remaining
        </span>
        {position.securityScore !== null && (
          <span
            className={`rounded border px-1.5 py-0.5 ${position.securityScore >= 80 ? "border-profit/50 text-profit" : position.securityScore >= 60 ? "border-gold-dim/50 text-gold" : "border-sell-dim/50 text-sell"}`}
          >
            Security {position.securityScore}
          </span>
        )}
        {position.liquidityUsdAtEntry !== null && (
          <span className="rounded border border-line-soft px-1.5 py-0.5 text-ink-faint">
            Liq at entry ${position.liquidityUsdAtEntry >= 1000 ? `${(position.liquidityUsdAtEntry / 1000).toFixed(1)}k` : position.liquidityUsdAtEntry.toFixed(0)}
          </span>
        )}
      </div>

      {!isOpen && (
        <div className="mt-3 flex items-center justify-between border-t border-line-soft pt-3 font-mono text-xs">
          <span className="text-ink-faint">{position.closeReason ?? STATUS_LABEL[position.status]}</span>
          <span className={`font-semibold tabular ${(pnl ?? 0) >= 0 ? "text-profit" : "text-sell"}`}>
            {pnl !== null ? `${pnl >= 0 ? "+" : ""}$${pnl.toFixed(2)}` : "—"}
          </span>
        </div>
      )}
    </div>
  );
}
